// @spec GHG-STR-067
import { createFileRoute } from '@tanstack/react-router';
import { Leaf, Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AssignGHGPermissionDialog,
  GHGPermissionAuditTab,
  GHGPermissionsList,
  useGHGFacilities,
  useGHGPermissions,
  useOrgMembers,
} from '@/features/settings/ghg-permissions';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/ghg-permissions')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.ghgPermissions'),
  }),
  component: GHGPermissionsPage,
});

/**
 * GHG Permissions settings page.
 *
 * Allows GHG Managers to assign and manage GHG-specific permissions for users.
 * Features:
 * - View all users with GHG access
 * - Assign new GHG permissions
 * - Edit or remove existing permissions
 * - Role-based access (Manager, Contributor, Auditor, Viewer)
 * - Facility-level access control for Contributors
 */
function GHGPermissionsPage() {
  const { t } = useTranslation('settings');
  const { organizations } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);

  const { data: membersData } = useOrgMembers();
  const { data: permissions } = useGHGPermissions();
  const { data: facilities } = useGHGFacilities();

  const assignedUserIds = new Set((permissions?.users ?? []).map((u) => u.userId));
  const availableUsers = (membersData?.members ?? [])
    .filter((m) => !assignedUserIds.has(m.userId))
    .map((m) => ({ id: m.userId, email: m.email, displayName: m.displayName }));
  const availableFacilities = (facilities ?? []).map((f) => ({ id: f.id, name: f.name }));

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-8">
        {/* Header */}
        <header className="flex items-start justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-semibold text-foreground">
              <Leaf className="h-8 w-8" aria-hidden="true" />
              {t('ghgPermissionsPage.title')}
            </h1>
            <p className="mt-1 text-muted-foreground">{t('ghgPermissionsPage.description')}</p>
          </div>

          <Button
            onClick={() => {
              setIsAssignDialogOpen(true);
            }}
            disabled={!activeOrg?.id}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('ghgPermissionsPage.addUser')}
          </Button>
        </header>

        <Tabs defaultValue="permissions" className="space-y-6">
          <TabsList>
            <TabsTrigger value="permissions">{t('ghgPermissionsPage.permissionsTab')}</TabsTrigger>
            <TabsTrigger value="audit">{t('ghgPermissionsPage.auditTab')}</TabsTrigger>
          </TabsList>

          <TabsContent value="permissions" className="space-y-6">
            {/* Role Descriptions Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('ghgPermissionsPage.roleOverviewTitle')}</CardTitle>
                <CardDescription>{t('ghgPermissionsPage.roleOverviewDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <h4 className="font-medium">{t('ghgPermissionsPage.managerTitle')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('ghgPermissionsPage.managerDescription')}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-medium">{t('ghgPermissionsPage.contributorTitle')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('ghgPermissionsPage.contributorDescription')}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-medium">{t('ghgPermissionsPage.auditorTitle')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('ghgPermissionsPage.auditorDescription')}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-medium">{t('ghgPermissionsPage.viewerTitle')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('ghgPermissionsPage.viewerDescription')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Permissions List Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('ghgPermissionsPage.usersWithAccessTitle')}</CardTitle>
                <CardDescription>
                  {t('ghgPermissionsPage.usersWithAccessDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <GHGPermissionsList />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <GHGPermissionAuditTab orgId={activeOrg?.id ?? ''} />
          </TabsContent>
        </Tabs>

        {/* Assign Permission Dialog */}
        {activeOrg?.id && (
          <AssignGHGPermissionDialog
            open={isAssignDialogOpen}
            onOpenChange={setIsAssignDialogOpen}
            availableUsers={availableUsers}
            availableFacilities={availableFacilities}
          />
        )}
      </div>
    </div>
  );
}
